/**
 * ConstructionProgressPublisher — shared publish-update flow.
 *
 * Extracted from ConstructorDashboard so admin and constructor can use the
 * exact same form (villa-specific or project-wide), with the same UX:
 * drag&drop, multi-upload, preview, remove, category, 8 MB client guard.
 *
 * Exports:
 *   - default `ConstructionProgressPublisher`  (form alone — admin embeds it
 *     with villa already in context).
 *   - named `ConstructionProgressPicker`       (wrapper with villa picker +
 *     sub-tabs + UploadsManager, used by constructor).
 *   - named `PROJECT_WIDE_VALUE`               (sentinel for villa picker).
 *
 * NO behavior changes from the constructor version — only relocation.
 */
import React, { useEffect, useState } from "react";
import { Section } from "@/pages/Shell";
import { api, formatError } from "@/lib/api";
import UploadsManager from "@/components/constructor/UploadsManager";
import ProgressGallery from "@/pages/investor/ProgressGallery";
import { Camera, Trash, UploadSimple, FolderOpen, Images } from "@phosphor-icons/react";

// Sentinel value for the "Todas las villas" option in the villa picker.
export const PROJECT_WIDE_VALUE = "__project_all__";

const DEFAULT_ACCENT = "#9C5236";

// Categories available for project-wide reports. Backend persists the
// enum value; the label is what the constructor sees in the picker.
const PROJECT_WIDE_CATEGORIES = [
  { value: "general",             label: "General" },
  { value: "preparacion_terreno", label: "Preparación del terreno" },
  { value: "permisos",            label: "Permisos" },
  { value: "cimentacion",         label: "Cimentación" },
  { value: "estructura",          label: "Estructura" },
  { value: "cubierta",            label: "Cubierta" },
  { value: "acabados",            label: "Acabados" },
  { value: "entrega",             label: "Entrega" },
  { value: "otro",                label: "Otro" },
];

const MAX_PHOTO_BYTES = 8 * 1024 * 1024;  // 8 MB — matches backend MAX_SIZE_BYTES (images/PDFs)
const MAX_VIDEO_BYTES = 12 * 1024 * 1024; // 12 MB — matches backend MAX_VIDEO_SIZE_BYTES
const MAX_VIDEOS_PER_UPDATE = 3;          // V1 cap

const ALLOWED_VIDEO_MIMES = ["video/mp4", "video/quicktime", "video/webm"];
const ACCEPT_ATTR = [
  "image/jpeg", "image/png", "image/webp",
  ...ALLOWED_VIDEO_MIMES,
].join(",");

function isVideoMime(m) {
  return typeof m === "string" && m.startsWith("video/");
}

function limitForFile(f) {
  return isVideoMime(f.type) ? MAX_VIDEO_BYTES : MAX_PHOTO_BYTES;
}

/**
 * Form component — publishes either a villa-specific progress update
 * (POST /progress + /construction/uploads) or a project-wide report
 * (POST /construction-reports with visibility_scope=project_all).
 */
export default function ConstructionProgressPublisher({
  villaId,
  villaName,
  isProjectWide = false,
  accent = DEFAULT_ACCENT,
  onPublished,
}) {
  const [phase, setPhase] = useState("structure");
  const [percentage, setPercentage] = useState(0);
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState([]); // [{base64, mime, filename, previewUrl, isVideo}]
  const [title, setTitle] = useState(""); // project-wide reports require a title
  const [category, setCategory] = useState("general"); // project-wide only
  const [takenAt, setTakenAt] = useState(""); // optional manual date for the update
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const ingestFiles = async (fileList) => {
    const raw = Array.from(fileList || []);
    const allowed = raw.filter(f =>
      f.type?.startsWith("image/") || isVideoMime(f.type)
    );
    if (!allowed.length) return;

    const tooBig = allowed.filter(f => f.size > limitForFile(f));
    const ok = allowed.filter(f => f.size <= limitForFile(f));

    const messages = [];
    if (tooBig.length) {
      const images = tooBig.filter(f => !isVideoMime(f.type));
      const videos = tooBig.filter(f => isVideoMime(f.type));
      if (images.length) {
        const names = images.map(f => `${f.name} (${(f.size / 1024 / 1024).toFixed(1)} MB)`).join(", ");
        messages.push(`Foto demasiado grande, máximo 8 MB: ${names}.`);
      }
      if (videos.length) {
        const names = videos.map(f => `${f.name} (${(f.size / 1024 / 1024).toFixed(1)} MB)`).join(", ");
        messages.push(`Vídeo demasiado grande, máximo 12 MB: ${names}. Reduce duración o calidad antes de subirlo.`);
      }
    }

    // Cap videos at MAX_VIDEOS_PER_UPDATE total across already-loaded + new.
    const currentVideos = photos.filter(p => p.isVideo).length;
    const incomingVideos = ok.filter(f => isVideoMime(f.type));
    const allowedVideosToAdd = Math.max(0, MAX_VIDEOS_PER_UPDATE - currentVideos);
    const acceptedVideos = incomingVideos.slice(0, allowedVideosToAdd);
    const rejectedVideos = incomingVideos.slice(allowedVideosToAdd);
    if (rejectedVideos.length) {
      messages.push(`Máximo ${MAX_VIDEOS_PER_UPDATE} vídeos por actualización. ${rejectedVideos.length} omitido(s).`);
    }
    const acceptedImages = ok.filter(f => !isVideoMime(f.type));
    const accepted = [...acceptedImages, ...acceptedVideos];

    setMsg(messages.length ? messages.join(" ") : "");
    if (!accepted.length) return;

    const next = await Promise.all(accepted.map(f => new Promise(res => {
      const r = new FileReader();
      r.onload = () => {
        const result = String(r.result || "");
        const comma = result.indexOf(",");
        res({
          base64: comma >= 0 ? result.slice(comma + 1) : result,
          mime: f.type,
          filename: f.name,
          previewUrl: result, // data URL for instant preview (image or video)
          isVideo: isVideoMime(f.type),
        });
      };
      r.readAsDataURL(f);
    })));
    setPhotos(prev => [...prev, ...next]);
  };

  const onFile = async (e) => {
    await ingestFiles(e.target.files);
    e.target.value = ""; // allow re-selecting the same file
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    await ingestFiles(e.dataTransfer?.files);
  };

  const removePhoto = (idx) => setPhotos(prev => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!villaId) { setMsg("Selecciona una villa primero."); return; }
    setBusy(true); setMsg("");
    try {
      if (isProjectWide) {
        // Project-wide path → POST /construction-reports with scope project_all.
        // Backend persists each inline photo via files_service (all-in-one).
        if (!title.trim() || !description.trim()) {
          setMsg("Título y descripción son obligatorios para una novedad general del proyecto.");
          setBusy(false);
          return;
        }
        const photosInline = photos.map(p => ({
          filename: p.filename,
          mime: p.mime,
          base64: p.base64,
        }));
        await api.post("/construction-reports", {
          title: title.trim(),
          summary: description.trim(),
          category,
          investor_user_id: null,
          villa_id: null,
          status: "on_track",
          progress_pct: 0,
          photo_urls: [],
          photo_file_ids: [],
          photos_inline: photosInline,
          visibility_scope: "project_all",
          confirm_project_all: true,
          taken_at: takenAt || null,
        });
        const count = photosInline.length;
        setMsg(`✓ Novedad general publicada${count ? ` con ${count} foto${count > 1 ? "s" : ""}` : ""}. Todos los inversores la verán en MyProject.`);
        setTitle("");
        setDescription("");
        setCategory("general");
        setPhotos([]);
        setTakenAt("");
      } else {
        // Villa-specific path.
        // 1) Upload each photo/video via /construction/uploads → collect file_ids.
        const uploadResults = await Promise.all(photos.map(p =>
          api.post("/construction/uploads", {
            villa_id: villaId,
            doc_type: p.isVideo ? "video" : "photo",
            phase,
            file: { filename: p.filename, mime: p.mime, base64: p.base64 },
            ...(takenAt ? { taken_at: takenAt } : {}),
          })
        ));
        const photoFileIds = uploadResults.map(r => r.data.file_id);

        // 2) Create the progress referencing the file_ids
        await api.post("/progress", {
          villa_id: villaId,
          phase,
          percentage: Number(percentage),
          description,
          photo_file_ids: photoFileIds,
        });

        const nImages = photos.filter(p => !p.isVideo).length;
        const nVideos = photos.filter(p => p.isVideo).length;
        const parts = [];
        if (nImages) parts.push(`${nImages} foto${nImages > 1 ? "s" : ""}`);
        if (nVideos) parts.push(`${nVideos} vídeo${nVideos > 1 ? "s" : ""}`);
        setMsg(`✓ Update published${parts.length ? ` con ${parts.join(" · ")}` : ""}. Los inversores ya pueden verlo.`);
        setPhotos([]);
        setDescription("");
        setTakenAt("");
      }
      onPublished?.();
    } catch (e) {
      setMsg(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="dv-card space-y-4 max-w-2xl">
      {isProjectWide ? (
        <div
          className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2"
          data-testid="progress-project-wide-banner"
        >
          Publicando para todas las villas — novedad general del proyecto.
        </div>
      ) : (
        villaName && (
          <div className="text-xs text-stone-500">
            Publishing for <span className="font-medium text-stone-800">{villaName}</span>
          </div>
        )
      )}

      {isProjectWide && (
        <>
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500">Categoría</label>
            <select
              className="dv-input mt-1"
              value={category}
              onChange={e => setCategory(e.target.value)}
              data-testid="progress-category-project"
            >
              {PROJECT_WIDE_CATEGORIES.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <div className="text-[11px] text-stone-500 mt-1" data-testid="progress-category-hint">
              Categoría:{" "}
              <span className="font-medium text-stone-700">
                {PROJECT_WIDE_CATEGORIES.find(c => c.value === category)?.label || category}
              </span>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500">Título</label>
            <input
              type="text"
              className="dv-input mt-1"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
              maxLength={200}
              data-testid="progress-title-project"
            />
          </div>
        </>
      )}

      {!isProjectWide && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500">Phase</label>
            <select className="dv-input mt-1" value={phase} onChange={e => setPhase(e.target.value)} data-testid="progress-phase">
              <option value="foundation">Foundation</option>
              <option value="structure">Structure</option>
              <option value="finishes">Finishes</option>
              <option value="delivery">Delivery</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500">% Complete</label>
            <input type="number" min="0" max="100" className="dv-input mt-1" value={percentage} onChange={e => setPercentage(e.target.value)} data-testid="progress-pct" />
          </div>
        </div>
      )}

      <div>
        <label className="text-xs uppercase tracking-widest text-stone-500">Fecha del avance (opcional)</label>
        <input
          type="date"
          className="dv-input mt-1"
          value={takenAt}
          onChange={e => setTakenAt(e.target.value)}
          data-testid="progress-taken-at"
        />
        <div className="text-[11px] text-stone-500 mt-1">
          Si dejas vacío se usa la fecha de subida.
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-stone-500">
          {isProjectWide ? "Descripción" : "Description"}
        </label>
        <textarea rows={3} className="dv-input mt-1" value={description} onChange={e => setDescription(e.target.value)} required data-testid="progress-desc" />
      </div>

      <div>
        <label className="text-xs uppercase tracking-widest text-stone-500">
          {isProjectWide ? "Fotos (opcional)" : "Photos"}
        </label>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragEnter={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`mt-1 flex flex-col items-center gap-2 p-4 rounded-md border-2 border-dashed transition-colors ${
            dragOver
              ? "border-stone-900 bg-stone-50"
              : "border-stone-300 bg-white"
          }`}
          data-testid="progress-dropzone"
        >
          <label className="dv-btn dv-btn-outline cursor-pointer">
            <UploadSimple size={16} /> Fotos y vídeos
            <input type="file" accept={ACCEPT_ATTR} multiple hidden onChange={onFile} data-testid="progress-files" />
          </label>
          {(() => {
            const nImages = photos.filter(p => !p.isVideo).length;
            const nVideos = photos.filter(p => p.isVideo).length;
            return (
              <span className="text-[11px] text-stone-500" data-testid="progress-photo-count">
                {nImages} foto{nImages === 1 ? "" : "s"} · {nVideos} vídeo{nVideos === 1 ? "" : "s"}
                {photos.length === 0 && (
                  <span className="ml-1 text-stone-400">· arrastra archivos aquí</span>
                )}
              </span>
            );
          })()}
          <span className="text-[10px] text-stone-400 text-center" data-testid="progress-size-hint">
            Foto ≤ 8 MB · Vídeo ≤ 12 MB · máx {MAX_VIDEOS_PER_UPDATE} vídeos por update.
            <br />Para mejores resultados, graba en 720p HD a 30 fps.
          </span>
        </div>
        {photos.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
            {photos.map((p, i) => (
              <div key={i} className="relative group" data-testid={`progress-media-${i}`}>
                {p.isVideo ? (
                  <video
                    src={p.previewUrl}
                    controls
                    preload="metadata"
                    playsInline
                    className="aspect-square w-full object-cover rounded-md bg-black"
                  />
                ) : (
                  <img src={p.previewUrl} alt="" className="aspect-square object-cover rounded-md" />
                )}
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Quitar archivo"
                  data-testid={`progress-photo-remove-${i}`}
                >
                  <Trash size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {msg && <div className="text-sm text-stone-600" data-testid="progress-msg">{msg}</div>}
      <button type="submit" disabled={busy || !villaId} className="dv-btn w-full text-white disabled:opacity-50" style={{ background: accent }} data-testid="progress-submit">
        {busy ? "Publishing…" : (isProjectWide ? "Publicar novedad general" : "Publish update")}
      </button>
    </form>
  );
}

function SubTabBtn({ active, onClick, icon: Icon, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
        active ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-700 hover:bg-stone-200"
      }`}
    >
      <Icon size={14} weight="light" />
      {children}
    </button>
  );
}

/**
 * Wrapper used by ConstructorDashboard: villa picker + sub-tabs (publish /
 * documents) + UploadsManager. Same behavior as before — just relocated.
 */
export function ConstructionProgressPicker({ accent = DEFAULT_ACCENT }) {
  const [villas, setVillas] = useState(null); // null = loading, [] = loaded empty
  const [villaId, setVillaId] = useState("");
  const [subTab, setSubTab] = useState("update"); // "update" | "documents"

  const loadVillas = () =>
    api.get("/villas")
      .then(r => {
        setVillas(r.data || []);
        return r.data || [];
      })
      .catch(() => { setVillas([]); return []; });

  useEffect(() => {
    loadVillas().then((list) => {
      if (list[0] && !villaId) setVillaId(list[0].villa_id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const villasLoaded = villas !== null;
  const villasList = villas || [];
  const selectedVilla = villasList.find((v) => v.villa_id === villaId) || null;

  return (
    <Section
      title="Construction updates"
      subtitle="Publish progress with photos, or upload plans/reports linked to this villa."
    >
      <EstadoGeneralDeObraPanel />

      {villasList.length >= 1 && (
        <div className="mb-5 max-w-md">
          <label className="text-xs uppercase tracking-widest text-stone-500">Villa</label>
          <select
            className="dv-input mt-1"
            value={villaId}
            onChange={(e) => setVillaId(e.target.value)}
            data-testid="progress-villa-shared"
          >
            <option value={PROJECT_WIDE_VALUE}>Todas las villas</option>
            {villasList.map(v => <option key={v.villa_id} value={v.villa_id}>{v.name}</option>)}
          </select>
        </div>
      )}

      {selectedVilla && villaId !== PROJECT_WIDE_VALUE && (
        <EstadoDeObraPanel villa={selectedVilla} onSaved={loadVillas} />
      )}

      <div className="flex gap-2 mb-5" data-testid="progress-subtabs">
        <SubTabBtn active={subTab === "update"} onClick={() => setSubTab("update")} icon={Camera} testId="subtab-update">
          Publish update
        </SubTabBtn>
        <SubTabBtn active={subTab === "documents"} onClick={() => setSubTab("documents")} icon={FolderOpen} testId="subtab-documents">
          Documents
        </SubTabBtn>
        <SubTabBtn active={subTab === "published"} onClick={() => setSubTab("published")} icon={Images} testId="subtab-published">
          Avances publicados
        </SubTabBtn>
      </div>

      {!villasLoaded && (
        <div className="dv-card text-center text-stone-500 py-10 text-sm">Loading villas…</div>
      )}
      {villasLoaded && villasList.length === 0 && (
        <div className="dv-card text-center text-stone-500 py-10 text-sm">
          No villas available. Contact admin.
        </div>
      )}
      {villaId && subTab === "update" && (
        <ConstructionProgressPublisher
          villaId={villaId}
          villaName={villasList.find(v => v.villa_id === villaId)?.name}
          isProjectWide={villaId === PROJECT_WIDE_VALUE}
          accent={accent}
        />
      )}
      {villaId && subTab === "documents" && villaId !== PROJECT_WIDE_VALUE && (
        <UploadsManager villaId={villaId} />
      )}
      {villaId === PROJECT_WIDE_VALUE && subTab === "documents" && (
        <div className="dv-card text-sm text-stone-500">
          Selecciona una villa concreta para gestionar documentos. La opción "Todas las villas" solo aplica al publicar updates.
        </div>
      )}
      {villaId && subTab === "published" && (
        // ProgressGallery itself handles both real villa ids and the
        // PROJECT_WIDE_VALUE sentinel (renders only project-wide reports).
        <ProgressGallery initialVillaId={villaId} hideVillaPicker />
      )}
    </Section>
  );
}

// Manual construction-state panel. Lives above the sub-tab switcher so the
// constructor/admin can update the % / current milestone / date / note the
// investor sees without leaving the workflow.
const HITO_OPTIONS = [
  { value: "preparacion", label: "Terreno" },
  { value: "cimentacion", label: "Cimentación" },
  { value: "estructura",  label: "Estructura" },
  { value: "cubierta",    label: "Cubierta" },
  { value: "acabados",    label: "Acabados" },
  { value: "entrega",     label: "Entrega" },
];

export function EstadoDeObraPanel({ villa, onSaved }) {
  const [pct, setPct] = useState("");
  const [hito, setHito] = useState("");
  const [fecha, setFecha] = useState("");
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    setPct(
      typeof villa?.overall_progress_pct_manual === "number"
        ? String(villa.overall_progress_pct_manual)
        : ""
    );
    setHito(villa?.current_milestone_key_manual || "");
    setFecha(villa?.last_progress_update_manual || "");
    setNota(villa?.progress_note_manual || "");
    setOkMsg("");
    setErrMsg("");
  }, [
    villa?.villa_id,
    villa?.overall_progress_pct_manual,
    villa?.current_milestone_key_manual,
    villa?.last_progress_update_manual,
    villa?.progress_note_manual,
  ]);

  const save = async () => {
    setOkMsg("");
    setErrMsg("");
    let pctPayload = null;
    if (String(pct).trim() !== "") {
      const n = Number(pct);
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        setErrMsg("El porcentaje debe estar entre 0 y 100.");
        return;
      }
      pctPayload = Math.round(n);
    }
    setSaving(true);
    try {
      await api.patch(`/villas/${villa.villa_id}/construction-state`, {
        overall_progress_pct_manual: pctPayload,
        last_progress_update_manual: fecha || null,
        current_milestone_key_manual: hito || null,
        progress_note_manual: nota.trim() || null,
      });
      setOkMsg("Estado de obra guardado.");
      onSaved?.();
    } catch (e) {
      setErrMsg(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!villa?.villa_id) return null;

  return (
    <div className="dv-card mb-5" data-testid="estado-obra-card">
      <div className="font-serif-dv text-lg mb-3">Estado de obra · {villa.name}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Progreso (%)</div>
          <input
            type="number"
            min={0}
            max={100}
            step={1}
            className="dv-input w-full"
            value={pct}
            onChange={(e) => { setPct(e.target.value); setOkMsg(""); }}
            placeholder="0 – 100"
            data-testid="estado-obra-pct"
          />
        </label>
        <label className="block">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Hito actual</div>
          <select
            className="dv-input w-full"
            value={hito}
            onChange={(e) => { setHito(e.target.value); setOkMsg(""); }}
            data-testid="estado-obra-hito"
          >
            <option value="">— Sin definir —</option>
            {HITO_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Fecha del último avance</div>
          <input
            type="date"
            className="dv-input w-full"
            value={fecha}
            onChange={(e) => { setFecha(e.target.value); setOkMsg(""); }}
            data-testid="estado-obra-fecha"
          />
        </label>
        <label className="block sm:col-span-2">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Nota / recomendación</div>
          <textarea
            className="dv-input w-full"
            rows={2}
            maxLength={500}
            value={nota}
            onChange={(e) => { setNota(e.target.value); setOkMsg(""); }}
            placeholder="Ej. Semana con lluvias fuertes. Próximo hito: cubierta."
            data-testid="estado-obra-nota"
          />
        </label>
      </div>
      <p className="text-xs text-stone-500 mt-3">
        Avance individual de esta villa: su inversor lo ve debajo del avance general. No cambia el avance general ni el de las demás villas. Las fotos y reportes no cambian el porcentaje automáticamente.
      </p>
      {errMsg && (
        <div className="mt-2 text-xs px-2 py-1.5 rounded border bg-red-50 border-red-200 text-red-800" data-testid="estado-obra-error">
          {errMsg}
        </div>
      )}
      {okMsg && (
        <div className="mt-2 text-xs px-2 py-1.5 rounded border bg-emerald-50 border-emerald-200 text-emerald-800" data-testid="estado-obra-ok">
          {okMsg}
        </div>
      )}
      <div className="mt-3">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="dv-btn dv-btn-primary text-sm"
          data-testid="estado-obra-save"
        >
          {saving ? "Guardando…" : "Guardar estado de obra"}
        </button>
      </div>
    </div>
  );
}

// Project-wide manual construction state. Independent of villa selector.
// Anything set here is what the investor sees in "Mi Proyecto".
export function EstadoGeneralDeObraPanel() {
  const [pct, setPct] = useState("");
  const [hito, setHito] = useState("");
  const [fecha, setFecha] = useState("");
  const [nota, setNota] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const applyState = (data) => {
    setPct(
      typeof data?.overall_progress_pct_manual === "number"
        ? String(data.overall_progress_pct_manual)
        : ""
    );
    setHito(data?.current_milestone_key_manual || "");
    setFecha(data?.last_progress_update_manual || "");
    setNota(data?.progress_note_manual || "");
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/construction-state")
      .then(({ data }) => { if (!cancelled) applyState(data); })
      .catch(() => { if (!cancelled) applyState(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setOkMsg("");
    setErrMsg("");
    let pctPayload = null;
    if (String(pct).trim() !== "") {
      const n = Number(pct);
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        setErrMsg("El porcentaje debe estar entre 0 y 100.");
        return;
      }
      pctPayload = Math.round(n);
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/construction-state", {
        overall_progress_pct_manual: pctPayload,
        last_progress_update_manual: fecha || null,
        current_milestone_key_manual: hito || null,
        progress_note_manual: nota.trim() || null,
      });
      applyState(data);
      setOkMsg("Estado general guardado.");
    } catch (e) {
      setErrMsg(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dv-card mb-5" data-testid="estado-general-card">
      <div className="font-serif-dv text-lg mb-1">Estado general de obra</div>
      <p className="text-xs text-stone-500 mb-3">
        Avance general del complejo: es la barra principal y la ven igual todos los inversores. No cambia el avance de ninguna villa. Las fotos y reportes no cambian el porcentaje automáticamente.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Progreso general (%)</div>
          <input
            type="number"
            min={0}
            max={100}
            step={1}
            className="dv-input w-full"
            value={pct}
            onChange={(e) => { setPct(e.target.value); setOkMsg(""); }}
            placeholder="0 – 100"
            disabled={loading}
            data-testid="estado-general-pct"
          />
        </label>
        <label className="block">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Hito actual</div>
          <select
            className="dv-input w-full"
            value={hito}
            onChange={(e) => { setHito(e.target.value); setOkMsg(""); }}
            disabled={loading}
            data-testid="estado-general-hito"
          >
            <option value="">— Sin definir —</option>
            {HITO_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Fecha del último avance</div>
          <input
            type="date"
            className="dv-input w-full"
            value={fecha}
            onChange={(e) => { setFecha(e.target.value); setOkMsg(""); }}
            disabled={loading}
            data-testid="estado-general-fecha"
          />
        </label>
        <label className="block sm:col-span-2">
          <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Nota del equipo</div>
          <textarea
            className="dv-input w-full"
            rows={2}
            maxLength={500}
            value={nota}
            onChange={(e) => { setNota(e.target.value); setOkMsg(""); }}
            placeholder="Ej. Semana con lluvias fuertes. Próximo hito: cubierta."
            disabled={loading}
            data-testid="estado-general-nota"
          />
        </label>
      </div>
      {errMsg && (
        <div className="mt-2 text-xs px-2 py-1.5 rounded border bg-red-50 border-red-200 text-red-800" data-testid="estado-general-error">
          {errMsg}
        </div>
      )}
      {okMsg && (
        <div className="mt-2 text-xs px-2 py-1.5 rounded border bg-emerald-50 border-emerald-200 text-emerald-800" data-testid="estado-general-ok">
          {okMsg}
        </div>
      )}
      <div className="mt-3">
        <button
          type="button"
          onClick={save}
          disabled={saving || loading}
          className="dv-btn dv-btn-primary text-sm"
          data-testid="estado-general-save"
        >
          {saving ? "Guardando…" : "Guardar estado general"}
        </button>
      </div>
    </div>
  );
}
